import React, { useEffect, useMemo, useState } from 'react';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import differenceWith from 'lodash/differenceWith';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import classnames from 'classnames';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';

import { useGetDashboard } from 'api/endpoints/getDashboard';
import { selectDisplayName } from 'features/logon/logonSlice';
import navigationPaths from 'engine/navigationPaths';

import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { Button } from 'components/ui';
import { Tile, Chart, Calendar, Documents } from './components';
import { AddDocumentButton } from 'components/AddDocumentButton/AddDocumentButton';

import * as Styled from './Dashboard.styles';
import { usePutKeyValueStore, useGetKeyValueStore} from 'api';
import FluentElement from 'components/fluent/fluentElement/FluentElement';

dayjs.extend(utc);

const ResponsiveGridLayout = WidthProvider(Responsive);
const layoutBreakpoint = 1000;

export const DashboardFluent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentDate = useMemo(() => dayjs().utc(true), []);

  const { mutate: updateKeyValueStore} = usePutKeyValueStore({
    onSuccess: () => {      
      refetchDashboardData();
      console.log(t('DASHBOARD_DATA_SAVED'));
    },
    onError: (e) => {
     console.log(t('ERROR_SAVING_DASHBOARD_DATA'));
    },
    
  });

  const { data: userData, refetch: refetchDashboardData} = useGetKeyValueStore({
    key: 'dashboardLayout'},{
    retry: false,
    onError: (e) => {     
      setSavedData([
        {
          id: 'calendar',
          position: { w: 4, h: 6, x: 0, y: 0 },
        },
        {
          id: 'chart',
          position: { w: 8, h: 6, x: 4, y: 0 },
        },
        {
          id: 'revenuePerYear',
          position: { w: 4, h: 2, x: 0, y: 6 },
        },
        {
          id: 'costsPerYear',
          position: { w: 4, h: 2, x: 4, y: 6 },
        },
        {
          id: 'balanceLoss',
          position: { w: 4, h: 2, x: 8, y: 6 },
        },
        {
          id: 'documents',
          position: { w: 12, h: 10, x: 0, y: 8 },
        },
      ])
    },
    onSuccess: (data) => {
      setSavedData(data.value.data);      
      console.log(t('DASHBOARD_DATA_LOADED'))
    }}   
  ) ;
  const [savedData, setSavedData] = useState([]); 
  const [editLayoutData, setEditLayoutData] = useState(null);
  const [currentLayoutWidth, setCurrentLayoutWidth] = useState(null);

  const { data: dashboardData } = useGetDashboard(currentDate);

  const displayName = useSelector(selectDisplayName);

  const revenuePerYear = useMemo(() => {
    return get(dashboardData, 'revenuePerYear', {});
  }, [dashboardData]);
  const costsPerYear = useMemo(() => {
    return get(dashboardData, 'costsPerYear', {});
  }, [dashboardData]);
  const balanceLoss = useMemo(() => {
    return get(dashboardData, 'balanceLoss', {});
  }, [dashboardData]);
  const chartData = useMemo(() => {
    return get(dashboardData, 'chartData', undefined);
  }, [dashboardData]);
  const recentDocuments = useMemo(() => {
    return get(dashboardData, 'recentDocuments', []);
  }, [dashboardData]);
  const periodicEvents = useMemo(() => {
    return get(dashboardData, 'periodicEvents', []);
  }, [dashboardData]);

  const allGridItems = useMemo(
    () => [
      {
        id: 'calendar',
        label: t('CALENDAR'),
        position: { w: 4, h: 6 },
        content: (
          <Styled.Box>
            <Calendar events={periodicEvents} />
          </Styled.Box>
        ),
      },
      {
        id: 'chart',
        label: t('CHART'),
        position: { w: 8, h: 6 },
        content: (
          <Styled.Box>
            <Chart chartData={chartData} />
          </Styled.Box>
        ),
      },
      {
        id: 'revenuePerYear',
        label: t('REVENUE_YEARLY'),
        position: { w: 4, h: 2 },
        content: (
          <Styled.Box>
            <Tile
              value={revenuePerYear.value}
              percent={revenuePerYear.yearOverYearPercentage}
              title={t('REVENUE_YEARLY')}
              year={revenuePerYear.year}
            />
          </Styled.Box>
        ),
      },
      {
        id: 'costsPerYear',
        label: t('COSTS_YEARLY'),
        position: { w: 4, h: 2 },
        content: (
          <Styled.Box>
            <Tile
              value={costsPerYear.value}
              percent={costsPerYear.yearOverYearPercentage}
              title={t('COSTS_YEARLY')}
              year={costsPerYear.year}
            />
          </Styled.Box>
        ),
      },
      {
        id: 'balanceLoss',
        label: t('BALANCE_LOSS'),
        position: { w: 4, h: 2 },
        content: (
          <Styled.Box>
            <Tile
              value={balanceLoss.value}
              percent={balanceLoss.yearOverYearPercentage}
              title={t('BALANCE_LOSS')}
              year={balanceLoss.year}
            />
          </Styled.Box>
        ),
      },
      {
        id: 'documents',
        label: t('DOCUMENTS_LATEST'),
        position: { w: 12, h: 10 },
        content: (
          <Styled.Box>
            <Documents data={recentDocuments} />
          </Styled.Box>
        ),
      },
    ],
    [periodicEvents, balanceLoss, chartData, costsPerYear, recentDocuments, revenuePerYear, t],
  );

  const [gridItems, setGridItems] = useState([]);

  useEffect(() => {
    setGridItems(
      (editLayoutData || savedData).map((item) => {
        return {
          ...(allGridItems.find((gridItemData) => gridItemData.id === item.id) || {}),
          ...item,
        };
      }),
    );
  }, [dashboardData, savedData, editLayoutData, setGridItems, allGridItems]);

  const layouts = useMemo(
    () => ({
      desktop: gridItems.map((gridItem) => ({
        i: gridItem.id,
        ...gridItem.position,
        static: !editLayoutData,
        // resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'],
      })),
      mobile: sortBy(gridItems, [(gridItem) => gridItem.position.y, (gridItem) => gridItem.position.x]).reduce(
        (acc, gridItem, index) => {
          const lastItem = acc[index - 1];

          return [
            ...acc,
            {
              i: gridItem.id,
              ...gridItem.position,
              static: !editLayoutData,
              x: 0,
              y: lastItem ? lastItem.h + lastItem.y : 0,
              w: 1,
            },
          ];
        },
        [],
      ),
    }),
    [gridItems, editLayoutData],
  );

  const onLayoutChange = (layout) =>
    !!editLayoutData &&
    setEditLayoutData((old) =>
      (old || []).map((widget) => {
        const layoutItem = layout.find((layoutItem) => layoutItem.i === widget.id);

        return {
          ...widget,
          position: {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          },
        };
      }),
    );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Styled.Wrapper>
        <Styled.Title>
          {t('WELCOME')}, {displayName}
          <AddDocumentButton
            className="addInvoiceButton"
            onClick={() => navigate(navigationPaths().InvoicesForm, { state: { mode: 'create' } })}
          >
            <AddOutlined /> {t('INVOICE_ADD')}
          </AddDocumentButton>
        </Styled.Title>

        {currentLayoutWidth > layoutBreakpoint && (
          <Styled.Buttons>
            {editLayoutData ? (
              <>
                {differenceWith(allGridItems, editLayoutData, (a, b) => a.id === b.id).map((item) => (
                  <Button
                    key={item.id}
                    variant="text"
                    onClick={() =>
                      setEditLayoutData((old) => [
                        ...(old || []),
                        {
                          id: item.id,
                          position: {
                            w: item.position.w,
                            h: item.position.h,
                            x: 0,
                            y: Infinity,
                          },
                        },
                      ])
                    }
                  >
                    + {item.label}
                  </Button>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditLayoutData(null);
                  }}
                >
                  {t('CANCEL')}
                </Button>
                <Button
                  onClick={() => {
                    updateKeyValueStore({ key: 'dashboardLayout', value: {data: editLayoutData} });
                    setSavedData(editLayoutData);
                    setEditLayoutData(null);
                  }}
                >
                  {t('SAVE')}
                </Button>
              </>
            ) : (
              <IconButton onClick={() => setEditLayoutData(savedData)}>
                <EditIcon />
              </IconButton>
            )}
          </Styled.Buttons>
        )}

        <Styled.LayoutWrapper className={classnames({ isEditing: !!editLayoutData })}>
          <ResponsiveGridLayout
            layouts={{ desktop: layouts.desktop, mobile: layouts.mobile }}
            breakpoints={{ desktop: 1000, mobile: 0 }}
            cols={{ desktop: 12, mobile: 1 }}
            rowHeight={60}
            onLayoutChange={onLayoutChange}
            onWidthChange={(width) => {
              setCurrentLayoutWidth(width);
              setEditLayoutData(null);
            }}
          >
            {gridItems?.map((gridItem) => (             
              
              <div key={gridItem.id} className={classnames('item')}>
                {gridItem.content}
                {editLayoutData && !gridItem.required && (
                  <Button
                    onClick={() =>
                      setEditLayoutData((old) => (old || []).filter((layoutItem) => layoutItem.id !== gridItem.id))
                    }
                    className="deleteButton"
                    variant="text"
                  >
                    <DeleteOutline />
                  </Button>
                )}
              </div>
                  
            ))}
          </ResponsiveGridLayout>
        </Styled.LayoutWrapper>
      </Styled.Wrapper>
    </DashboardLayout>
  );
};
